import { z } from 'zod';

/** Job vocabulary: job types and their required documents (PRD → Build approach → Cargo; gitaumoses4/chukua#22). */

export const requiredDocumentTypeInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
});
export type RequiredDocumentTypeInput = z.infer<typeof requiredDocumentTypeInputSchema>;

export const JOB_TYPE_FAMILIES = ['container', 'loose'] as const;
export type JobTypeFamily = (typeof JOB_TYPE_FAMILIES)[number];

export const jobTypeInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
  family: z.enum(JOB_TYPE_FAMILIES),
  requiredDocumentTypeCodes: z.array(z.string().trim().min(1)).default([]),
});
export type JobTypeInput = z.infer<typeof jobTypeInputSchema>;

/**
 * A job's state machine (PRD → Job intake → States; gitaumoses4/chukua#29).
 * Planned/InProgress/Delivered are reached only through domain events from
 * dispatch/tracking (M3/M5) — no request shape here ever sets them directly.
 */
export const JOB_STATUSES = ['enquiry', 'quoted', 'confirmed', 'planned', 'in_progress', 'delivered', 'closed', 'on_hold', 'cancelled'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** How an enquiry reached the office (PRD → Job intake; gitaumoses4/chukua#30). */
export const JOB_SOURCES = ['phone', 'whatsapp', 'email', 'release_order', 'walk_in'] as const;
export type JobSource = (typeof JOB_SOURCES)[number];

/**
 * `POST /jobs` — quick-create (gitaumoses4/chukua#30): only `customerId`/`jobTypeCode`
 * are required, a job is created in Enquiry, and status is never a field here. Everything
 * else is fillable now or later through `PATCH /jobs/:id` (`updateJobSchema`).
 */
export const createJobSchema = z.object({
  customerId: z.string().uuid(),
  jobTypeCode: z.string().trim().min(1).max(32),
  originZoneCode: z.string().trim().min(1).max(32).optional(),
  destinationZoneCode: z.string().trim().min(1).max(32).optional(),
  requestedDate: z.iso.date().optional(),
  customerDeliveryDeadline: z.iso.date().optional(),
  source: z.enum(JOB_SOURCES).optional(),
  contactName: z.string().trim().min(1).max(120).optional(),
  contactPhone: z.string().trim().min(1).max(32).optional(),
  requestedQuantity: z.number().int().positive().max(999).optional(),
});
export type CreateJobInput = z.infer<typeof createJobSchema>;

/** `PATCH /jobs/:id` — fills in whatever quick-create left blank. `customerId`/`jobTypeCode` are immutable post-create. */
export const updateJobSchema = createJobSchema.omit({ customerId: true, jobTypeCode: true });
export type UpdateJobInput = z.infer<typeof updateJobSchema>;

/** `POST /jobs/:id/{cancel,hold}` — same shape as `recordOverrideSchema`'s reason field. */
export const jobReasonSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type JobReasonInput = z.infer<typeof jobReasonSchema>;
